import React, {FC, Fragment, ReactElement} from 'react';
import {PageHeader} from "../../components/PageHeader";
import {GUTTER} from "../../constants";
import {SearchField} from "../../components/DataTable/SearchField";
import {Space, Tooltip} from "antd";
import {ExportButton} from "../../components/DataTable/ExportButton";
import {Filter, QueryFilter} from "../../components/DataTable/Filters/types";
import {FilterButton} from "../../components/DataTable/FilterButton";
import {DataTable} from "../../components/DataTable/DataTable";
import {NumericFilter} from "../../components/DataTable/Filters/NumericFilter";
import {ColumnsType} from "antd/lib/table";
import {Cohort} from "../../types";
import {Translate, useLabels} from "../../hooks/Labels";
import {InfoCircleOutlined} from "@ant-design/icons";
import isNumeric from "antd/lib/_util/isNumeric";
import {buildCohortTree} from "./utils";
import {PreserveQueryLink as Link} from "../../components/PreserveQueryLink";
import {hasEnoughUsage} from "../../hooks/AnalyticsAccess";
import { Organisation, useOrganisation } from '../../layout/OrganisationInformationProvider';

const filters = (t: Translate): Filter<any>[] => ([
    {
        attribute: 'total_students',
        title: t('student_pl'),
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt']
        },
    },
    {
        attribute: 'remaining_seats',
        title: 'Available Seats',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt']
        },
    },
    // {
    //     attribute: 'average_score',
    //     title: 'Score',
    //     component: NumericFilter,
    //     config: {
    //         filters: ['exact', 'range', 'lt', 'gt']
    //     },
    // },
    {
        attribute: 'course_completion',
        title: 'Completion',
        component: NumericFilter,
        config: {
            filters: ['exact', 'range', 'lt', 'gt']
        },
    }
]);

const columns = (t: Translate, organisation: Organisation): ColumnsType<Cohort> => ([
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        render: (text, record) => hasEnoughUsage(record.total_students, organisation.minimum_students_for_analytics)
            ? <Link to={record.id.toString()}>{text}</Link>
            : text
    },
    {
        title: t('student_pl'),
        dataIndex: 'total_students',
        key: 'total_students',
        sorter: true,
    },
    {
        title: 'Available Seats',
        dataIndex: 'remaining_seats',
        key: 'remaining_seats',
        sorter: true,
    },
    // {
    //     title: 'Score',
    //     dataIndex: 'average_score',
    //     key: 'average_score',
    //     sorter: true,
    //     render: text => isNumeric(text) ? `${text}%` : 'N/A'
    // },
    {
        title: 'Completion',
        dataIndex: 'course_completion',
        key: 'course_completion',
        sorter: true,
        render: (text, record) => hasEnoughUsage(record.total_students, organisation.minimum_students_for_analytics)
            ? (isNumeric(text) ? `${text}%` : 'N/A')
            : <Space>
                <span>{t('analytics_not_available_title')}</span>
                <Tooltip 
                    title={
                        <Fragment>
                            <p>
                                {t('analytics_not_available_explanation')}
                            </p>
                            <p>
                                {t('analytics_not_available_explanation_extra')}
                            </p>
                        </Fragment>
                    }
                >
                    <InfoCircleOutlined/>
                </Tooltip>
            </Space>
    },
    // {
    //     title: 'Avg. Time Spent',
    //     dataIndex: 'average_time_spent',
    //     key: 'average_time_spent',
    //     sorter: true,
    //     render: text => isNumeric(text) ? `${Math.round(text)}m` : 'N/A'
    // },
]);

export const CohortsPage: FC = (): ReactElement => {
    const {t} = useLabels();
    const organisation = useOrganisation();
    return (
        <Fragment>
            <PageHeader title={t('cohort_pl')}/>
            <div>
                <div style={{display: "flex", justifyContent: "space-between", marginBottom: GUTTER[0]}}>
                    <SearchField placeholder={`${t('cohort')} name`}/>
                    <Space>
                        <ExportButton
                            url='/reports/cohorts/'
                            filenamePrefix='cohorts'
                            defaultFilters={{exclude: 'invitation_code'}}
                            searchParamFilters={[QueryFilter.COURSES]}
                        />
                        <FilterButton filters={filters(t)}/>
                    </Space>
                </div>
                <DataTable
                    columns={columns(t, organisation)}
                    query='/reports/cohorts/'
                    searchParamFilters={[QueryFilter.COURSES]}
                    dataSourceTransform={buildCohortTree}
                    expandable={{ defaultExpandAllRows: true }}
                    pagination={false}
                />
            </div>
        </Fragment>
    );
};
